import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';
import { CircleX as CircleXIcon } from '@parrot-software/design-system/icons';

interface ReportErrorStateProps {
  message?: string;
  onRetry?: () => void;
}

/**
 * ReportErrorState - Error state component for reports
 *
 * Displays a user-friendly error message with an error icon and retry button
 * when data loading fails.
 *
 * @param {ReportErrorStateProps} props - Component properties
 * @param {string} [props.message] - Custom error message translation key
 * @param {Function} [props.onRetry] - Callback function executed when retry button is clicked
 * @returns {React.ReactElement} Error state component
 *
 * @example
 * <ReportErrorState
 *   message="reports:messages.errorLoadingData"
 *   onRetry={() => refetch()}
 * />
 */
export const ReportErrorState = ({ message, onRetry }: ReportErrorStateProps) => {
  const { t } = useTranslation(['common', 'reports']);

  const errorMessage = message
    ? t(message)
    : t('reportCommon:messages.errorLoadingData', 'Ocurrió un error al cargar la información');

  const handleRetryClick = () => {
    if (onRetry) {
      onRetry();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="report-error-state">
      <div className="flex h-18 w-8 items-center justify-center">
        <CircleXIcon className="h-8 w-8 text-destructive" strokeWidth={1.5} />
      </div>

      <p className="text-lg font-medium w-64">{errorMessage}</p>

      {onRetry && <Button onClick={handleRetryClick}>{t('common:buttons.retry', 'Reintentar')}</Button>}
    </div>
  );
};
